/** @jsx jsx */
import { type MutableRefObject, memo, type ReactNode, useState, forwardRef, type Ref } from 'react';
import { css, keyframes, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Popup, type PopupComponentProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useCellHoverCallbacks, useCellHoverHandlers } from '../controllers/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../constants.tsx';
import { PopupContent } from './popup-content/index.tsx';

export type Props<T extends HTMLElement = HTMLElement> = {
	content?: ReactNode;
	children: (props: { ref: Ref<T> }) => ReactNode;
	contentRef?: MutableRefObject<T | null>;
	entryPointActions?: EntryPointActions;
	isInteractive?: boolean;
	shouldGetParentRef?: boolean;
	isFixedWidth?: boolean;
	onHoverOut?: () => void;
};

const HoverPopoverComponent = <T extends HTMLElement>({
	content,
	children,
	contentRef,
	entryPointActions,
	isInteractive,
	shouldGetParentRef,
	isFixedWidth,
	onHoverOut,
}: Props<T>) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	useCellHoverCallbacks({
		contentRef,
		onPreload: entryPointActions?.preload,
		onLoad: entryPointActions?.load,
		onPopupToggle: setIsPopupOpen,
		shouldGetParentRef,
		onHoverOut,
	});

	if (content) {
		return (
			<UFOSegment name="jira-list-hover-popover" mode="list">
				<JSErrorBoundary
					id="jira-list-hover-popover"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					fallback="flag"
				>
					<Popup
						content={({ update }) => <PopupContent update={update}>{content}</PopupContent>}
						popupComponent={forwardRef<HTMLDivElement, PopupComponentProps>(
							({ children: popupChildren, isReferenceHidden, ...restProps }, ref) => {
								if (isReferenceHidden && isPopupOpen) {
									setIsPopupOpen(false);
								}

								return (
									<div
										css={[
											containerStyles,
											isInteractive && interactiveStyles,
											isFixedWidth && fixedWidthStyles,
										]}
										{...restProps}
										ref={ref}
									>
										{popupChildren}
									</div>
								);
							},
						)}
						// eslint-disable-next-line jsx-a11y/no-autofocus
						autoFocus={false}
						isOpen={isPopupOpen}
						trigger={({ ref }) => children({ ref })}
						placement="bottom-start"
					/>
				</JSErrorBoundary>
			</UFOSegment>
		);
	}

	return typeof children === 'function' ? children({ ref: null }) : children;
};

// @see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-656596623
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const HoverPopover = memo(HoverPopoverComponent) as typeof HoverPopoverComponent;

export const CellHoverRenderer = ({
	children,
	shouldFillHeight,
}: {
	children: ReactNode;
	shouldFillHeight?: boolean;
}) => {
	const { handleMouseEnter, handleMouseLeave } = useCellHoverHandlers();
	const singleLineRowHeightProps = {
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		onBlur: handleMouseLeave,
	};

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<Box xcss={[contentStyles, shouldFillHeight && fillHeight]} {...singleLineRowHeightProps}>
			{children}
		</Box>
	);
};

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const contentStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const fillHeight = xcss({
	height: '100%',
});

const interactiveStyles = css({
	marginBlock: token('space.negative.100'),
	paddingBlock: token('space.150'),
	pointerEvents: 'auto',
});

const containerStyles = css({
	marginBlock: token('space.100'),
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	pointerEvents: 'none',
});

const fixedWidthStyles = css({
	width: '300px',
});
